
import React from 'react';
import { Home, Search, Package, User } from 'lucide-react';
import { User as UserType, SiteSettings } from '../types';

interface MobileBottomNavProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onOpenSearch: () => void;
  onToggleChat: () => void;
  onOpenAuth: () => void;
  currentUser: UserType | null;
  isChatOpen?: boolean;
  settings?: SiteSettings;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ 
  currentPage, 
  onNavigate, 
  onOpenSearch, 
  onToggleChat, 
  onOpenAuth,
  currentUser,
  isChatOpen = false,
  settings
}) => {
  const whatsappNumber = settings?.whatsappNumber || '5516992091604';
  const cleanNumber = whatsappNumber.replace(/\D/g, '');
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const message = encodeURIComponent(`Olá! Estou no site ${currentUrl} e gostaria de mais informações.`);
  const whatsappUrl = `https://wa.me/${cleanNumber}?text=${message}`;

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[100]">
      {/* Container principal: Borda cinza ultra-fina e sombra mais suave */}
      <div className="bg-white border-t border-gray-100 shadow-[0_-5px_20px_rgba(0,0,0,0.04)] h-16 flex items-center justify-around relative px-2 rounded-t-[24px]">
        
        {/* INÍCIO */}
        <button 
          onClick={() => onNavigate('home')}
          className="flex flex-col items-center gap-0.5 flex-1 transition-all active:scale-95"
        >
          <div className={`p-1 rounded-xl transition-colors ${currentPage === 'home' ? 'text-primary' : 'text-gray-300'}`}>
            <Home size={20} strokeWidth={2} />
          </div>
          <span className={`text-[8px] font-bold uppercase tracking-wider ${currentPage === 'home' ? 'text-primary' : 'text-gray-400'}`}>Início</span>
        </button>

        {/* PRODUTOS */}
        <button 
          onClick={() => onNavigate('produtos')}
          className="flex flex-col items-center gap-0.5 flex-1 transition-all active:scale-95"
        >
          <div className={`p-1 rounded-xl transition-colors ${currentPage === 'produtos' ? 'text-primary' : 'text-gray-300'}`}>
            <Package size={20} strokeWidth={2} />
          </div>
          <span className={`text-[8px] font-bold uppercase tracking-wider ${currentPage === 'produtos' ? 'text-primary' : 'text-gray-400'}`}>Produtos</span>
        </button>

        {/* BOTÃO CENTRAL (BUSCAR) - Reduzido e Refinado */}
        <div className="relative -top-5 flex flex-col items-center">
            <button 
                onClick={onOpenSearch}
                className="w-14 h-14 bg-primary text-white rounded-[20px] shadow-lg shadow-primary/30 flex items-center justify-center transform transition-transform active:scale-90 border-[4px] border-white"
            >
                <Search size={22} strokeWidth={2.5} />
            </button>
            <span className="mt-1 text-[8px] font-bold uppercase tracking-wider text-primary">Buscar</span>
        </div>

        {/* WHATSAPP */}
        <a 
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center gap-0.5 flex-1 transition-all active:scale-95 justify-center h-full"
        >
          <div className="p-1 rounded-xl transition-colors">
            <svg 
              viewBox="0 0 32 32" 
              className="w-5 h-5" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M16 0C7.163 0 0 7.163 0 16c0 2.825.733 5.479 2.014 7.787L0 32l8.442-2.014A15.91 15.91 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0z" 
                fill="#fff" 
              />
              <path 
                d="M16 0.6C7.495 0.6 0.6 7.495 0.6 16c0 2.707.7 5.247 1.928 7.452L0.4 30.8l7.716-1.842A15.2 15.2 0 0 0 16 31.4c8.505 0 15.4-6.895 15.4-15.4S24.505 0.6 16 0.6z" 
                fill="#25D366" 
              />
              <path 
                d="M21.507 18.533c-.3-.15-1.773-.875-2.047-.975-.275-.1-.475-.15-.675.15s-.775.975-.95 1.175-.35.225-.65.075c-.3-.15-1.267-.467-2.413-1.488-.892-.796-1.494-1.779-1.669-2.079-.175-.3-.019-.463.131-.612.135-.134.3-.35.45-.525.15-.175.2-.3.3-.5s.05-.375-.025-.525-.675-1.625-.925-2.225c-.244-.584-.491-.505-.675-.514-.175-.008-.375-.01-.575-.01s-.525.075-.8.375c-.275.3-1.05 1.025-1.05 2.5s1.075 2.9 1.225 3.1c.15.2 2.113 3.227 5.119 4.525.715.309 1.273.494 1.708.632.718.229 1.371.196 1.887.119.575-.086 1.773-.725 2.023-1.425.25-.7.25-1.3.175-1.425-.075-.125-.275-.2-.575-.35z" 
                fill="#fff" 
              />
            </svg>
          </div>
          <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400">WhatsApp</span>
        </a>

        {/* CONTA */}
        <button 
          onClick={() => {
            if (currentUser) {
              onNavigate('admin');
            } else {
              onOpenAuth();
            }
          }}
          className="flex flex-col items-center gap-0.5 flex-1 transition-all active:scale-95"
        >
          <div className={`p-1 rounded-xl transition-colors ${currentPage === 'admin' ? 'text-primary' : 'text-gray-300'}`}>
            <User size={20} strokeWidth={2} />
          </div>
          <span className={`text-[8px] font-bold uppercase tracking-wider ${currentPage === 'admin' ? 'text-primary' : 'text-gray-400'}`}>
            {currentUser ? 'Perfil' : 'Conta'}
          </span>
        </button>

      </div>
      {/* Área de segurança */}
      <div className="h-[env(safe-area-inset-bottom)] bg-white w-full"></div>
    </div>
  );
};

export default MobileBottomNav;
